import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import ThemeSidebar from './components/feed/ThemeSidebar';
import Loader from './components/Loader';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PointsProvider } from './contexts/PointsContext';
import FloatingPoints from './components/FloatingPoints';
import { LanguageProvider } from './i18n';
import { Analytics } from "@vercel/analytics/react"

// Lazy load des composants pour réduire le bundle initial (code splitting)
const FeedPage = lazy(() => import('./components/feed/FeedPage'));
const VotePage = lazy(() => import('./components/VotePage'));
const FeedPreviewPage = lazy(() => import('./components/feed/FeedPreviewPage'));
const ProfileView = lazy(() => import('./components/ProfileView'));
const BatchView = lazy(() => import('./components/BatchView'));
const PrivacyPage = lazy(() => import('./components/PrivacyPage'));
const TermsPage = lazy(() => import('./components/TermsPage'));

function AddPage() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <AddThumbnailWrapper />;
}

// Wrapper pour le lazy loading de AddThumbnailView avec son hook
const AddThumbnailWrapper = lazy(() => import('./components/AddThumbnailWrapper'));

// La home est un jeu qui doit tenir dans l'écran : pas de scroll, pas de footer.
// Ses liens légaux vivent dans la sidebar des thèmes.
function AppShell() {
  const { pathname } = useLocation();

  // La grille est un jeu qui doit tenir dans l'écran : pas de scroll, pas de
  // footer. Le mode vote garde un défilement normal, mais partage la même
  // navigation de gauche pour qu'on puisse passer de l'un à l'autre.
  const isFeed = pathname === '/';
  const hasSidebar = isFeed || pathname === '/vote';

  const routes = (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path="/" element={<FeedPage />} />
        <Route path="/vote" element={<VotePage />} />

        <Route path="/add" element={<AddPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/:username" element={<ProfileView />} />
        <Route path="/:username/:batchId" element={<BatchView />} />
        <Route path="/:username/:batchId/preview" element={<FeedPreviewPage />} />
      </Routes>
    </Suspense>
  );

  return (
    <div className={`app ${isFeed ? 'app-feed' : ''}`}>
      <Header />
      <main id="main-content" className={`main-content ${isFeed ? 'main-content-feed' : ''}`}>
        {hasSidebar ? (
          <div className={`feed-page ${isFeed ? '' : 'feed-page-scrollable'}`}>
            <ThemeSidebar />
            {routes}
          </div>
        ) : routes}
        {!isFeed && <Footer />}
      </main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <PointsProvider>
            <AppShell />
            <FloatingPoints />
          </PointsProvider>
          <Analytics />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
